import { useState } from "react";
import { 
  User, Star, MapPin, Calendar, Award, Heart, MessageCircle, Edit, 
  Briefcase, Users, Package, Clock, Plus, Eye, ArrowLeft, MessageSquare, 
  Edit3, Trash2 
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import BottomNavigation from "@/components/BottomNavigation";

const ProfileFixed = () => {
  const [activeTab, setActiveTab] = useState("services");
  const navigate = useNavigate();

  // Mock user profile data
  const profileData = {
    name: "Sarah Moonlight",
    email: "sarah@example.com",
    avatar: "https://images.pexels.com/photos/3763188/pexels-photo-3763188.jpeg?auto=compress&cs=tinysrgb&w=400",
    location: "Amsterdam, Nederland",
    joinDate: "January 2024",
    verified: true,
    bio: "Mindfulness coach en wellness enthusiast die bewuste vrouwen helpt hun kracht te vinden door meditatie, ademwerk en holistische healing praktijken.",
    stats: {
      services: 3,
      products: 2,
      appointments: 12,
      reviews: 18,
      rating: 4.9
    }
  };

  // Mock services data
  const myServices = [
    {
      id: 1,
      title: "Mindful Life Coaching Session",
      price: 85,
      duration: "90 min",
      bookings: 8,
      rating: 4.9,
      status: "Active",
      image: "https://images.pexels.com/photos/3760514/pexels-photo-3760514.jpeg?auto=compress&cs=tinysrgb&w=300"
    },
    {
      id: 2,
      title: "Breathwork & Meditation Circle",
      price: 35,
      duration: "60 min",
      bookings: 15,
      rating: 4.8,
      status: "Active",
      image: "https://images.pexels.com/photos/3822622/pexels-photo-3822622.jpeg?auto=compress&cs=tinysrgb&w=300"
    },
    {
      id: 3,
      title: "Chakra Alignment Session",
      price: 75,
      duration: "75 min",
      bookings: 3,
      rating: 5.0,
      status: "Draft",
      image: "https://images.pexels.com/photos/3820360/pexels-photo-3820360.jpeg?auto=compress&cs=tinysrgb&w=300"
    }
  ];

  // Mock products data
  const myProducts = [
    {
      id: 1,
      title: "Crystal Meditation Set",
      price: 89.99,
      stock: 3,
      sold: 12,
      rating: 4.8,
      status: "Active",
      image: "https://images.pexels.com/photos/6646918/pexels-photo-6646918.jpeg?auto=compress&cs=tinysrgb&w=300"
    },
    {
      id: 2,
      title: "Sage Cleansing Bundle",
      price: 24.99,
      stock: 8,
      sold: 25,
      rating: 4.9,
      status: "Active",
      image: "https://images.pexels.com/photos/6192525/pexels-photo-6192525.jpeg?auto=compress&cs=tinysrgb&w=300"
    }
  ];

  // Mock appointments data
  const myAppointments = [
    {
      id: 1,
      clientName: "Emma S.",
      service: "Mindful Life Coaching",
      date: "2024-01-15",
      time: "14:00",
      status: "Upcoming",
      type: "Online"
    },
    {
      id: 2,
      clientName: "Lisa M.",
      service: "Breathwork Session",
      date: "2024-01-12",
      time: "10:00",
      status: "Completed",
      type: "In-person"
    },
    {
      id: 3,
      clientName: "Maya K.",
      service: "Chakra Alignment",
      date: "2024-01-18",
      time: "16:30",
      status: "Upcoming",
      type: "Online"
    }
  ];

  // Mock reviews data
  const myReviews = [
    {
      id: 1,
      clientName: "Anna B.",
      service: "Mindful Life Coaching",
      rating: 5,
      comment: "Sarah's coaching transformeerde mijn perspectief op het leven. Haar zachte begeleiding en praktische tools hielpen me helderheid en doel te vinden. Zeer aanbevolen!",
      date: "3 dagen geleden"
    },
    {
      id: 2,
      clientName: "Sophie L.",
      service: "Crystal Meditation Set",
      rating: 5,
      comment: "Prachtige kristallen met geweldige energie! De meditatie gids was super behulpzaam. Perfect voor mijn spirituele praktijk.",
      date: "1 week geleden"
    },
    {
      id: 3,
      clientName: "Rachel D.",
      service: "Breathwork Session",
      rating: 4,
      comment: "Ongelooflijk helende sessie. Sarah creëerde zo'n veilige, voedende ruimte. Ik voelde me zo veel lichter nadien.",
      date: "2 weken geleden"
    }
  ];

  // FUNCTIONAL EYE ICON HANDLERS
  const handleViewService = (service: any) => {
    console.log("👁️ Viewing service details:", service.id);
    navigate(`/service/${service.id}`);
    toast.success(`Service bekijken: ${service.title}`, {
      description: "Navigating naar service details pagina",
      action: {
        label: "Open in nieuw tabblad",
        onClick: () => window.open(`/service/${service.id}`, '_blank')
      }
    });
  };

  const handleViewProduct = (product: any) => {
    console.log("👁️ Viewing product details:", product.id);
    navigate(`/product/${product.id}`);
    toast.success(`Product bekijken: ${product.title}`, {
      description: "Navigating naar product details pagina",
      action: {
        label: "Open in nieuw tabblad",
        onClick: () => window.open(`/product/${product.id}`, '_blank')
      }
    });
  };

  const handleViewAppointment = (appointment: any) => {
    console.log("👁️ Viewing appointment details:", appointment.id);
    toast.info(`Afspraak Details: ${appointment.service}`, {
      description: `${appointment.date} om ${appointment.time} met ${appointment.clientName} (${appointment.type})`,
      action: {
        label: "Ga naar agenda",
        onClick: () => navigate('/agenda')
      }
    });
  };

  const handleViewReview = (review: any) => {
    console.log("👁️ Viewing review details:", review.id);
    toast.info(`Review van ${review.clientName}`, {
      description: `${review.rating} sterren voor ${review.service}`,
      action: {
        label: "Alle reviews bekijken",
        onClick: () => {
          console.log("Navigate to all reviews");
          toast.success("Alle reviews laden...");
        }
      }
    });
  };

  const handleEditService = (service: any) => {
    toast.info(`Bewerken: ${service.title}`);
    navigate(`/edit-service/${service.id}`);
  };

  const handleEditProduct = (product: any) => {
    toast.info(`Bewerken: ${product.title}`);
    navigate(`/edit-product/${product.id}`);
  };

  const handleDeleteService = (service: any) => {
    if (window.confirm(`Weet je zeker dat je "${service.title}" wilt verwijderen?`)) {
      toast.success(`Service "${service.title}" verwijderd!`);
    }
  };

  const handleDeleteProduct = (product: any) => {
    if (window.confirm(`Weet je zeker dat je "${product.title}" wilt verwijderen?`)) {
      toast.success(`Product "${product.title}" verwijderd!`);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Active":
        return "bg-green-100 text-green-700";
      case "Draft":
        return "bg-yellow-100 text-yellow-700";
      case "Upcoming":
        return "bg-blue-100 text-blue-700";
      case "Completed":
        return "bg-cream-100 text-navy-700";
      default:
        return "bg-cream-100 text-navy-700";
    }
  };

  return (
    <div className="min-h-screen bg-elegant pb-24">
      {/* Header */}
      <div className="glass-effect border-b border-cream-300">
        <div className="px-6 py-6">
          {/* Back Button */}
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="p-2 hover:bg-cream-100 text-navy-700"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-serif text-navy-800">Mijn Profiel</h1>
              <p className="text-sm text-navy-600">
                Beheer je WOM profiel en aanbod
              </p>
            </div>
          </div>

          {/* Profile Header */}
          <div className="card-premium p-4 mb-6">
            <div className="text-center">
              <div className="mb-2">
                <h1 className="script-text text-4xl text-navy-800">
                  Mijn Profiel
                </h1>
              </div>
              <p className="text-sm text-navy-600">
                Laat zien wie je bent als woman on a mission
              </p>
            </div>
          </div>

          {/* Profile Info */}
          <div className="flex items-start gap-6 mb-6">
            <Avatar className="h-24 w-24">
              <AvatarImage src={profileData.avatar} alt={profileData.name} />
              <AvatarFallback className="bg-cream-200 text-navy-700 text-2xl">
                {profileData.name.split(' ').map(n => n[0]).join('')}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h2 className="text-xl font-serif text-navy-800">{profileData.name}</h2>
                {profileData.verified && (
                  <Badge variant="secondary" className="bg-gold-100 text-gold-700">
                    Verified
                  </Badge>
                )}
                <div className="gradient-gold text-white px-2 py-1 rounded-full text-xs font-bold tracking-wide">
                  WOM
                </div>
              </div>

              <p className="font-medium mb-1 text-navy-700">Woman on a Mission</p>
              <p className="mb-2 text-navy-600">{profileData.location}</p>
              <p className="text-sm mb-3 text-navy-600">Lid sinds {profileData.joinDate}</p>
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1">
                  <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  <span className="font-medium text-navy-700">{profileData.stats.rating}</span>
                  <span className="text-navy-600">({profileData.stats.reviews} reviews)</span>
                </div>
              </div>
            </div>

            <Button 
              variant="outline"
              className="btn-elegant-secondary"
            >
              <Edit3 className="h-4 w-4 mr-2" />
              Profiel bewerken
            </Button>
          </div>

          {/* Bio */}
          <p className="leading-relaxed mb-6 max-w-2xl text-navy-700">
            {profileData.bio}
          </p>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-navy-800">{profileData.stats.services}</div>
              <div className="text-sm text-navy-600">Diensten</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-navy-800">{profileData.stats.products}</div>
              <div className="text-sm text-navy-600">Producten</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-navy-800">{profileData.stats.appointments}</div>
              <div className="text-sm text-navy-600">Afspraken</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-navy-800">{profileData.stats.reviews}</div>
              <div className="text-sm text-navy-600">Reviews</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Content */}
      <div className="px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-6 bg-cream-200">
            <TabsTrigger value="services" className="flex items-center gap-2 data-[state=active]:bg-white text-navy-700">
              <Briefcase className="h-4 w-4" />
              <span className="hidden sm:inline">Diensten</span>
            </TabsTrigger>
            <TabsTrigger value="products" className="flex items-center gap-2 data-[state=active]:bg-white text-navy-700">
              <Package className="h-4 w-4" />
              <span className="hidden sm:inline">Producten</span>
            </TabsTrigger>
            <TabsTrigger value="appointments" className="flex items-center gap-2 data-[state=active]:bg-white text-navy-700">
              <Calendar className="h-4 w-4" />
              <span className="hidden sm:inline">Afspraken</span>
            </TabsTrigger>
            <TabsTrigger value="reviews" className="flex items-center gap-2 data-[state=active]:bg-white text-navy-700">
              <MessageSquare className="h-4 w-4" />
              <span className="hidden sm:inline">Reviews</span>
            </TabsTrigger>
          </TabsList>

          {/* My Services Tab */}
          <TabsContent value="services">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-serif text-navy-800">Mijn Diensten</h2>
                <Button className="btn-elegant-primary">
                  <Plus className="h-4 w-4 mr-2" />
                  Dienst toevoegen
                </Button>
              </div>
              {myServices.map((service) => (
                <Card key={service.id} className="card-premium-hover">
                  <CardContent className="p-4">
                    <div className="flex gap-4">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-20 h-20 rounded-lg object-cover"
                      />
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-serif font-semibold text-navy-800">{service.title}</h3>
                          <Badge className={getStatusColor(service.status)}>
                            {service.status}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm mb-2 text-navy-600">
                          <span>€{service.price}</span>
                          <span>•</span>
                          <span>{service.duration}</span>
                          <span>•</span>
                          <span>{service.bookings} boekingen</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                          <span className="text-sm font-medium">{service.rating}</span>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 justify-center items-end">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="hover:bg-navy-100 text-navy-600" 
                          title="Bewerk dienst" 
                          onClick={(e) => {
                            e.stopPropagation(); 
                            handleEditService(service);
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="hover:bg-rose-100 text-rose-600" 
                          title="Verwijder dienst" 
                          onClick={(e) => {
                            e.stopPropagation(); 
                            handleDeleteService(service);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="hover:bg-cream-100 text-navy-700" 
                          title="👁️ Bekijk dienst" 
                          onClick={(e) => {
                            e.stopPropagation(); 
                            handleViewService(service);
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* My Products Tab */}
          <TabsContent value="products">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-serif text-navy-800">Mijn Producten</h2>
                <Button className="btn-elegant-primary">
                  <Plus className="h-4 w-4 mr-2" />
                  Product toevoegen
                </Button>
              </div>
              {myProducts.map((product) => (
                <Card key={product.id} className="card-premium-hover">
                  <CardContent className="p-4">
                    <div className="flex gap-4">
                      <img
                        src={product.image}
                        alt={product.title}
                        className="w-20 h-20 rounded-lg object-cover"
                      />
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-serif font-semibold text-navy-800">{product.title}</h3>
                          <Badge className={getStatusColor(product.status)}>
                            {product.status}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm mb-2 text-navy-600">
                          <span>€{product.price}</span>
                          <span>•</span>
                          <span>{product.stock} op voorraad</span>
                          <span>•</span>
                          <span>{product.sold} verkocht</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                          <span className="text-sm font-medium">{product.rating}</span>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 justify-center items-end">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="hover:bg-navy-100 text-navy-600" 
                          title="Bewerk product" 
                          onClick={(e) => {
                            e.stopPropagation(); 
                            handleEditProduct(product);
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="hover:bg-rose-100 text-rose-600" 
                          title="Verwijder product" 
                          onClick={(e) => {
                            e.stopPropagation(); 
                            handleDeleteProduct(product);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="hover:bg-cream-100 text-navy-700" 
                          title="👁️ Bekijk product" 
                          onClick={(e) => {
                            e.stopPropagation(); 
                            handleViewProduct(product);
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* My Appointments Tab */}
          <TabsContent value="appointments">
            <div className="space-y-4">
              <h2 className="text-xl font-serif text-navy-800">Mijn Afspraken</h2>
              {myAppointments.map((appointment) => (
                <Card key={appointment.id} className="card-premium-hover">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-serif font-semibold text-navy-800">{appointment.service}</h3>
                          <Badge className={getStatusColor(appointment.status)}>
                            {appointment.status}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-navy-600">
                          <span>met {appointment.clientName}</span>
                          <span>•</span>
                          <span>{new Date(appointment.date).toLocaleDateString()} om {appointment.time}</span>
                          <span>•</span>
                          <span>{appointment.type}</span>
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="hover:bg-cream-100 text-navy-700" 
                        onClick={() => handleViewAppointment(appointment)}
                        title="👁️ Bekijk afspraak details"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews">
            <div className="space-y-4">
              <h2 className="text-xl font-serif text-navy-800">Reviews</h2>
              {myReviews.map((review) => (
                <Card key={review.id} className="card-premium">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-navy-800">{review.clientName}</span>
                          <span className="text-sm text-navy-600">•</span>
                          <span className="text-sm text-navy-600">{review.service}</span>
                        </div>
                        <div className="flex items-center gap-1 mb-2">
                          {[...Array(5)].map((_, i) => (
                            <Star 
                              key={i} 
                              className={`h-4 w-4 ${i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                            />
                          ))}
                        </div>
                      </div>
                      <span className="text-sm text-navy-600">{review.date}</span>
                    </div>
                    <p className="leading-relaxed text-navy-700 mb-3">{review.comment}</p>
                    <div className="flex justify-end">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="hover:bg-cream-100 text-navy-700" 
                        onClick={() => handleViewReview(review)}
                        title="👁️ Bekijk review details"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Bottom Navigation */}
      <BottomNavigation />
    </div>
  );
};

export default ProfileFixed;